import React from "react";

import { ClientSvgChooser, Home2026FeaturedWorkBlock } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Svg } from "../../../Svg";
import { revealClassName, useReveal } from "../../shared/Reveal";
import { useFrame } from "../../shared/frame";
import { usePrefersReducedMotion } from "../../shared/reducedMotion";
import { TICKER_ARTWORK } from "./artwork";

import styles from "./index.module.scss";

/** Seconds for one row to pass. Slow. */
const ROW_SECONDS = 46;

interface Props {
    logos: Home2026FeaturedWorkBlock["logos"];
    /** Placement, from the section */
    className?: string;
}

interface LogoProps {
    name: Exclude<ClientSvgChooser, "">;
}

const Logo = (props: LogoProps) => {
    const art = TICKER_ARTWORK[props.name];
    if (!art) return <Svg name={props.name} className={styles.logo} />;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={art.viewBox}
            fill="currentColor"
            className={styles.logo}
        >
            {art.path}
        </svg>
    );
};

/**
 * The client logos, scrolling continuously (featuredwork.js). Two identical
 * rows, so the loop is seamless: the track moves one row's width and wraps.
 * It moves only near the viewport, and holds still under reduced motion.
 * Decorative, as in the prototype: hidden from assistive technology.
 */
export const LogoTicker = (props: Props) => {
    const { ref, shown } = useReveal<HTMLDivElement>({ enter: 0.92 });
    const [revealed] = shown;
    const trackRef = React.useRef<HTMLDivElement>(null);
    const rowRef = React.useRef<HTMLDivElement>(null);
    const reduced = usePrefersReducedMotion();
    const [near, setNear] = React.useState(false);
    const motion = React.useRef({ x: 0, rowWidth: 0 });

    React.useEffect(() => {
        const root = ref.current;
        const row = rowRef.current;
        if (!root || !row) return;
        const nearObserver = new IntersectionObserver(
            (entries) => setNear(entries[entries.length - 1].isIntersecting),
            { rootMargin: "200px 0px" },
        );
        nearObserver.observe(root);
        const sizeObserver = new ResizeObserver((entries) => {
            motion.current.rowWidth =
                entries[entries.length - 1].borderBoxSize[0].inlineSize;
        });
        sizeObserver.observe(row);
        return () => {
            nearObserver.disconnect();
            sizeObserver.disconnect();
        };
    }, [ref]);

    useFrame((_now, dt) => {
        const track = trackRef.current;
        const m = motion.current;
        if (!track || !m.rowWidth) return;
        m.x -= dt * (m.rowWidth / ROW_SECONDS);
        if (m.x <= -m.rowWidth) m.x += m.rowWidth;
        track.style.transform = `translate3d(${m.x.toFixed(2)}px, 0, 0)`;
    }, near && !reduced);

    const logos = (props.logos ?? []).flatMap((logo) =>
        logo.value ? [{ id: logo.id, name: logo.value }] : [],
    );
    const row = logos.map((logo) => <Logo key={logo.id} name={logo.name} />);

    return (
        <div
            ref={ref}
            className={concatClassNames([
                styles.root,
                revealClassName(revealed),
                props.className,
            ])}
            aria-hidden="true"
        >
            <div ref={trackRef} className={styles.track}>
                <div ref={rowRef} className={styles.row}>
                    {row}
                </div>
                <div className={styles.row}>{row}</div>
            </div>
        </div>
    );
};
